import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import {
  COLOR_VALUE,
  RECIPE_TYPE,
  RESTORATION_METHOD,
} from '@/pages/getRecipe/shadeGuide/utils/constants'
import {
  FORM_FIELDS,
  type FormData,
} from '@/pages/getRecipe/shadeGuide/utils/formTypes'

interface ShadeGuideState {
  shadeGuideActiveStep: number
  form: { [key in keyof FormData]?: any }
  shadeGuideRecipeOutput: any
  generation_id?: string | number
  restorationMethodOnRecipeGenerate: string | null
  isGrayscale: Record<string, boolean>
}

const initialState: ShadeGuideState = {
  shadeGuideActiveStep: 0,
  form: {
    [FORM_FIELDS.RECIPE_TYPE]: RECIPE_TYPE.SINGLE_ZONE,
    [FORM_FIELDS.RESTORATION_METHOD]: RESTORATION_METHOD.STAINING,
    [FORM_FIELDS.PREPARATION_COLOR]: COLOR_VALUE.Vita3DMaster,
    [FORM_FIELDS.TARGET_COLOR]: COLOR_VALUE.Vita3DMaster,
    [FORM_FIELDS.AVAILABLE_SPACE_CERVICAL]: 0.5,
    [FORM_FIELDS.AVAILABLE_SPACE_INCISAL]: 0.5,
    [FORM_FIELDS.AVAILABLE_SPACE_MIDDLE]: 0.5,
  },
  shadeGuideRecipeOutput: null,
  restorationMethodOnRecipeGenerate: null,
  isGrayscale: {},
}

export const shadeGuideSlice = createSlice({
  name: 'shadeGuide',
  initialState,
  reducers: {
    setShadeGuideActiveStep: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        shadeGuideActiveStep: action.payload,
      }
    },
    updateshadeGuideFormValue: (
      state,
      action: PayloadAction<{ name: keyof FormData; value: any }>,
    ) => {
      return {
        ...state,
        form: {
          ...state.form,
          [action.payload.name]: action.payload.value,
        },
      }
    },
    setShadeGuideRecipeOutput: (
      state,
      action: PayloadAction<{
        result: any
        generation_id?: string | number
      }>,
    ) => {
      return {
        ...state,
        shadeGuideRecipeOutput: action.payload.result,
        generation_id: action.payload.generation_id,
      }
    },
    setRestorationMethodOnRecipeGenerate: (
      state,
      action: PayloadAction<string>,
    ) => {
      return {
        ...state,
        restorationMethodOnRecipeGenerate: action.payload,
      }
    },
    resetState: (state) => {
      return {
        ...initialState,
        shadeGuideRecipeOutput: state.shadeGuideRecipeOutput,
        restorationMethodOnRecipeGenerate:
          state.restorationMethodOnRecipeGenerate,
        generation_id: state.generation_id,
      }
    },
    toggleGrayscale: (state, action: PayloadAction<{ imageId: string }>) => {
      const { imageId } = action.payload
      const currentGrayscale = state.isGrayscale || {}
      return {
        ...state,
        isGrayscale: {
          ...currentGrayscale,
          [imageId]: !currentGrayscale[imageId],
        },
      }
    },
    setGrayscale: (state, action: PayloadAction<Record<string, boolean>>) => {
      return {
        ...state,
        isGrayscale: action.payload,
      }
    },
  },
})

export const {
  setShadeGuideActiveStep,
  updateshadeGuideFormValue,
  setShadeGuideRecipeOutput,
  resetState,
  setRestorationMethodOnRecipeGenerate,
  toggleGrayscale,
  setGrayscale,
} = shadeGuideSlice.actions

export default shadeGuideSlice.reducer
